import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  writeBatch,
  arrayUnion,
  increment,
  QueryConstraint,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Feedback, FeedbackThread, UserProfile } from '../types';

export interface FeedbackFilters {
  status?: 'all' | 'open' | 'in-progress' | 'resolved' | 'closed';
  category?: 'all' | 'bug' | 'feature' | 'general';
  sortBy?: 'recent' | 'popular' | 'discussed';
}

export class FeedbackService {
  private static readonly FEEDBACK_COLLECTION = 'feedback';
  private static readonly THREADS_COLLECTION = 'feedbackThreads';

  /**
   * Submit new feedback
   */
  static async submitFeedback(
    userId: string,
    userEmail: string,
    profile: UserProfile | null,
    data: {
      category: 'bug' | 'feature' | 'general';
      title: string;
      description: string;
    }
  ): Promise<string> {
    console.log('[submitFeedback] Creating new feedback:', data);

    const feedbackData: any = {
      userId,
      userEmail,
      category: data.category,
      title: data.title,
      description: data.description,
      status: 'open',
      upvotes: 0,
      upvotedBy: [],
      threadCount: 0,
    };

    // Only add optional fields if they have values
    if (profile?.displayName) {
      feedbackData.userDisplayName = profile.displayName;
    }
    if (profile?.handle) {
      feedbackData.userHandle = profile.handle;
    }
    if (profile?.profilePicUrl) {
      feedbackData.userProfilePic = profile.profilePicUrl;
    }

    const docRef = await addDoc(collection(db, this.FEEDBACK_COLLECTION), {
      ...feedbackData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    console.log('[submitFeedback] Feedback created with ID:', docRef.id);
    return docRef.id;
  }

  /**
   * Get feedback list with filters
   */
  static async getFeedback(filters?: FeedbackFilters): Promise<Feedback[]> {
    const constraints: QueryConstraint[] = [];

    // Apply status filter
    if (filters?.status && filters.status !== 'all') {
      constraints.push(where('status', '==', filters.status));
    }

    // Apply category filter
    if (filters?.category && filters.category !== 'all') {
      constraints.push(where('category', '==', filters.category));
    }

    // Apply sorting
    switch (filters?.sortBy) {
      case 'popular':
        constraints.push(orderBy('upvotes', 'desc'));
        break;
      case 'discussed':
        constraints.push(orderBy('threadCount', 'desc'));
        break;
      case 'recent':
      default:
        constraints.push(orderBy('createdAt', 'desc'));
    }

    // Add limit
    constraints.push(limit(50));

    const q = query(collection(db, this.FEEDBACK_COLLECTION), ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      adminRespondedAt: doc.data().adminRespondedAt?.toDate(),
    } as Feedback));
  }

  /**
   * Get single feedback with threads
   */
  static async getFeedbackDetail(feedbackId: string): Promise<{
    feedback: Feedback | null;
    threads: FeedbackThread[];
  }> {
    // Get feedback
    const feedbackDoc = await getDoc(doc(db, this.FEEDBACK_COLLECTION, feedbackId));
    if (!feedbackDoc.exists()) {
      return { feedback: null, threads: [] };
    }

    const feedback: Feedback = {
      id: feedbackDoc.id,
      ...feedbackDoc.data(),
      createdAt: feedbackDoc.data().createdAt?.toDate() || new Date(),
      updatedAt: feedbackDoc.data().updatedAt?.toDate() || new Date(),
      adminRespondedAt: feedbackDoc.data().adminRespondedAt?.toDate(),
    } as Feedback;

    // Get threads
    const threadsQuery = query(
      collection(db, this.THREADS_COLLECTION),
      where('feedbackId', '==', feedbackId),
      orderBy('createdAt', 'asc')
    );
    const threadsSnapshot = await getDocs(threadsQuery);
    
    const threads = threadsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    } as FeedbackThread));

    return { feedback, threads };
  }

  /**
   * Upvote feedback
   */
  static async upvoteFeedback(feedbackId: string, userId: string): Promise<void> {
    console.log('[upvoteFeedback] User', userId, 'upvoting feedback', feedbackId);
    
    const feedbackRef = doc(db, this.FEEDBACK_COLLECTION, feedbackId);
    
    await updateDoc(feedbackRef, {
      upvotes: increment(1),
      upvotedBy: arrayUnion(userId),
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Upvote thread
   */
  static async upvoteThread(threadId: string, userId: string): Promise<void> {
    console.log('[upvoteThread] User', userId, 'upvoting thread', threadId);
    
    const threadRef = doc(db, this.THREADS_COLLECTION, threadId);
    
    await updateDoc(threadRef, {
      upvotes: increment(1),
      upvotedBy: arrayUnion(userId),
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Add reply to feedback
   */
  static async addReply(
    feedbackId: string,
    userId: string,
    userEmail: string,
    profile: UserProfile | null,
    content: string,
    isAdmin: boolean = false
  ): Promise<void> {
    console.log('[addReply] Adding reply to feedback', feedbackId);

    const batch = writeBatch(db);

    // Add thread - build object dynamically to avoid undefined fields
    const threadData: any = {
      feedbackId,
      userId,
      userEmail,
      content,
      isAdminReply: isAdmin,
      upvotes: 0,
      upvotedBy: [],
    };

    // Only add optional fields if they have values
    if (profile?.displayName) {
      threadData.userDisplayName = profile.displayName;
    }
    if (profile?.handle) {
      threadData.userHandle = profile.handle;
    }
    if (profile?.profilePicUrl) {
      threadData.userProfilePic = profile.profilePicUrl;
    }

    const threadRef = doc(collection(db, this.THREADS_COLLECTION));
    batch.set(threadRef, {
      ...threadData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Update thread count on feedback
    const feedbackRef = doc(db, this.FEEDBACK_COLLECTION, feedbackId);
    batch.update(feedbackRef, {
      threadCount: increment(1),
      updatedAt: serverTimestamp(),
    });

    await batch.commit();
    console.log('[addReply] Reply added successfully');
  }

  /**
   * Update feedback (owner can edit title/description)
   */
  static async updateFeedback(
    feedbackId: string,
    updates: {
      title?: string;
      description?: string;
    }
  ): Promise<void> {
    console.log('[updateFeedback] Updating feedback', feedbackId, updates);

    await updateDoc(doc(db, this.FEEDBACK_COLLECTION, feedbackId), {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Admin functions
   */

  /**
   * Update feedback status (admin only)
   */
  static async updateStatus(
    feedbackId: string,
    status: 'open' | 'in-progress' | 'resolved' | 'closed'
  ): Promise<void> {
    console.log('[updateStatus] Admin updating status to', status);

    await updateDoc(doc(db, this.FEEDBACK_COLLECTION, feedbackId), {
      status,
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Set priority (admin only)
   */
  static async setPriority(
    feedbackId: string,
    priority: 'low' | 'medium' | 'high'
  ): Promise<void> {
    console.log('[setPriority] Admin setting priority to', priority);

    await updateDoc(doc(db, this.FEEDBACK_COLLECTION, feedbackId), {
      priority,
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Add admin response (admin only)
   */
  static async addAdminResponse(
    feedbackId: string,
    response: string
  ): Promise<void> {
    console.log('[addAdminResponse] Admin adding response');

    await updateDoc(doc(db, this.FEEDBACK_COLLECTION, feedbackId), {
      adminResponse: response,
      adminRespondedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Delete feedback (admin only)
   */
  static async deleteFeedback(feedbackId: string): Promise<void> {
    console.log('[deleteFeedback] Admin deleting feedback', feedbackId);

    const batch = writeBatch(db);

    // Delete feedback
    batch.delete(doc(db, this.FEEDBACK_COLLECTION, feedbackId));

    // Delete all threads
    const threadsQuery = query(
      collection(db, this.THREADS_COLLECTION),
      where('feedbackId', '==', feedbackId)
    );
    const threadsSnapshot = await getDocs(threadsQuery);
    threadsSnapshot.forEach(doc => {
      batch.delete(doc.ref);
    });

    await batch.commit();
    console.log('[deleteFeedback] Feedback and threads deleted');
  }

  /**
   * Check if user has upvoted
   */
  static hasUserUpvoted(item: Feedback | FeedbackThread, userId: string): boolean {
    return item.upvotedBy.includes(userId);
  }
}